// Minimal native IndexedDB wrapper — just enough to store the offline cart
// catalog and a queue of sales made while offline, without pulling in a
// dependency for something this small.
const DB_NAME = 'saydalani-offline';
const DB_VERSION = 1;

export const STORES = {
    CATALOG: 'catalog',
    PENDING_ORDERS: 'pendingOrders',
};

let dbPromise = null;

export function openDb() {
    if (dbPromise) return dbPromise;

    dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;

            if (!db.objectStoreNames.contains(STORES.CATALOG)) {
                const catalog = db.createObjectStore(STORES.CATALOG, { keyPath: 'id' });
                catalog.createIndex('barcode', 'barcode', { unique: false });
            }

            if (!db.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
                db.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'localId' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });

    return dbPromise;
}

export async function getAll(storeName) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const request = tx.objectStore(storeName).getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

export async function getByIndex(storeName, indexName, value) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const request = tx.objectStore(storeName).index(indexName).get(value);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
    });
}

export async function put(storeName, value) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put(value);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function remove(storeName, key) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function clearStore(storeName) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function bulkPut(storeName, values) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        values.forEach((v) => store.put(v));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}
